import React, { useState } from 'react';
import { CheckCircleIcon, ShieldCheckIcon, GlobeIcon, ChevronDownIcon, ChevronUpIcon, SendIcon } from 'lucide-react';
import BackButton from '../../components/UI/BackButton';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
const VendorLicensing = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const benefits = [{
    title: 'Earn Royalties',
    description: 'Receive a percentage of every sale made using your licensed intellectual property.',
    icon: CheckCircleIcon
  }, {
    title: 'Brand Control',
    description: 'Review and approve all designs before they go to production to ensure brand consistency.',
    icon: ShieldCheckIcon
  }, {
    title: 'Wider Reach',
    description: 'Get your book in front of more readers through merchandise and product collaborations.',
    icon: GlobeIcon
  }];
  const faqs = [{
    question: 'What percentage do I earn?',
    answer: 'Our standard licensing agreement offers authors 10-15% royalties on net sales, depending on the product category and volume.'
  }, {
    question: 'Who handles production and shipping?',
    answer: 'The vendor handles all manufacturing, inventory, shipping, and customer service. You simply approve the designs and collect royalties.'
  }, {
    question: 'Can I revoke a license?',
    answer: 'Yes, all licensing agreements include termination clauses that allow you to revoke rights with standard notice periods.'
  }, {
    question: 'Is it exclusive?',
    answer: 'You can choose between exclusive and non-exclusive licenses. Exclusive licenses typically command higher royalty rates.'
  }];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    // In a real app, this would send data to backend
  };
  return <div className={`min-h-screen py-8 ${dk.page}`} data-id="element-22086" data-mp="2eplz001vr0ew">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22087" data-mp="29rc5ruzpu9vx">
        <div className="mb-6" data-id="element-22088" data-mp="29rkhhjk3r95j">
          <BackButton data-id="element-22089" data-mp="24s8ye3w9jrks" />
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12" data-id="element-22090" data-mp="24u267mim4hud">
          <h1 className={`text-4xl font-bold mb-4 ${dk.textHeading}`} data-id="element-22091" data-mp="32fpzhyjruk1o">
            License Your Book for Merchandise
          </h1>
          <p className={`text-xl max-w-2xl mx-auto ${dk.textSecondary}`} data-id="element-22092" data-mp="2xi7qk85r84to">
            Partner with verified vendors to create official merchandise for
            your readers. Expand your brand while maintaining full creative
            control.
          </p>
        </div>

        {/* Benefits Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16" data-id="element-22093" data-mp="3l791q35z1u2n">
          {benefits.map((benefit, index) => <div key={index} className={`p-6 rounded-xl shadow-sm border text-center ${dk.card}`} data-id="element-22094" data-mp="3gefw7d2oihpi" data-mpi={index}>
              <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${rt.bgMedium}`} data-id="element-22095" data-mp="3spgpixzu5nvq">
                <benefit.icon className={`h-6 w-6 ${rt.textPrimary}`} data-id="element-22096" data-mp="1idxy5g35fkz" />
              </div>
              <h3 className={`text-lg font-bold mb-2 ${dk.textHeading}`} data-id="element-22097" data-mp="6kd23hjnggiz">
                {benefit.title}
              </h3>
              <p className={dk.textSecondary} data-id="element-22098" data-mp="bg26pzaj0nm5">{benefit.description}</p>
            </div>)}
        </div>

        {/* How It Works */}
        <div className={`mb-16 p-8 rounded-xl border ${dk.card}`} data-id="element-22099" data-mp="k9x24fluizol">
          <h2 className={`text-2xl font-bold mb-8 text-center ${dk.textHeading}`} data-id="element-22100" data-mp="p99xjoqs9f2s">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-22101" data-mp="u9nw8g6hvco5">
            {[{
            step: '1',
            title: 'Apply',
            desc: 'Submit your book details for review.'
          }, {
            step: '2',
            title: 'Get Matched',
            desc: 'Connect with interested vendors.'
          }, {
            step: '3',
            title: 'Approve',
            desc: 'Review and approve product designs.'
          }, {
            step: '4',
            title: 'Earn',
            desc: 'Receive monthly royalty payouts.'
          }].map((item, index) => <div key={index} className="text-center relative" data-id="element-22102" data-mp="18300zvlqv6hm" data-mpi={index}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold ${rt.bgDark} text-white`} data-id="element-22103" data-mp="1m1g5dfw4wkwl">
                  {item.step}
                </div>
                <h3 className={`font-bold mb-2 ${dk.textPrimary}`} data-id="element-22104" data-mp="2xhwl7ablp3jz">
                  {item.title}
                </h3>
                <p className={`text-sm ${dk.textSecondary}`} data-id="element-22105" data-mp="30xr8bv5krpn5">{item.desc}</p>
                {index < 3 && <div className={`hidden md:block absolute top-5 left-1/2 w-full h-0.5 -z-10 ${dk.isDark ? 'bg-gray-700' : 'bg-gray-200'}`} style={{
              marginLeft: '50%'
            }} data-id="element-22106" data-mp="35xdi1um72nr7" />}
              </div>)}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-16" data-id="element-22107" data-mp="3onc0n4nmbz93">
          <h2 className={`text-2xl font-bold mb-6 ${dk.textHeading}`} data-id="element-22108" data-mp="3onlha1mrwu24">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4" data-id="element-22109" data-mp="39xwc7vbk85wk">
            {faqs.map((faq, index) => <div key={index} className={`border rounded-lg overflow-hidden ${dk.card}`} data-id="element-22110" data-mp="3ewh3h58u3f8v" data-mpi={index}>
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className={`w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none ${dk.textPrimary}`} data-id="element-22111" data-mp="6fmaqilr3b8i">
                  <span className="font-medium" data-id="element-22112" data-mp="g52zh4x6omno">{faq.question}</span>
                  {openFaq === index ? <ChevronUpIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-22113" data-mp="c2eoorowt5o2" /> : <ChevronDownIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-22114" data-mp="73v16muo3ltl" />}
                </button>
                {openFaq === index && <div className={`px-6 pb-4 ${dk.textSecondary}`} data-id="element-22115" data-mp="3tkhfpweieaii">
                    {faq.answer}
                  </div>}
              </div>)}
          </div>
        </div>

        {/* Contact Form */}
        <div className={`p-8 rounded-xl border shadow-sm ${dk.card}`} data-id="element-22116" data-mp="3kkkkfbxm6deh">
          <div className="text-center mb-8" data-id="element-22117" data-mp="3fkzlh1ig251r">
            <h2 className={`text-2xl font-bold mb-2 ${dk.textHeading}`} data-id="element-22118" data-mp="3fjwxp2gf4iq2">
              Start Licensing Today
            </h2>
            <p className={dk.textSecondary} data-id="element-22119" data-mp="3anpp11wn0t4f">
              Fill out the form below to get started with our licensing program.
            </p>
          </div>

          {submitted ? <div className={`text-center py-12 ${dk.textPrimary}`} data-id="element-22120" data-mp="1v4ibovyy5dbr">
              <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-green-100 text-green-600`} data-id="element-22121" data-mp="202tlj6p8rntz">
                <CheckCircleIcon className="h-8 w-8" data-id="element-22122" data-mp="17d36mvfschwt" />
              </div>
              <h3 className="text-xl font-bold mb-2" data-id="element-22123" data-mp="17aqgvw3sd397">Application Received!</h3>
              <p className={dk.textSecondary} data-id="element-22124" data-mp="1ccfthg6npoze">
                Thanks for your interest. Our team will review your application
                and get back to you within 2-3 business days.
              </p>
              <button onClick={() => setSubmitted(false)} className={`mt-6 text-sm font-medium ${rt.accentLink}`} data-id="element-22125" data-mp="1l5j1h8yj99mn">
                Submit another application
              </button>
            </div> : <form onSubmit={handleSubmit} className="space-y-6 max-w-lg mx-auto" data-id="element-22126" data-mp="3i57at14qii2z">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-22127" data-mp="3d8gtybl3vigi">
                <div data-id="element-22128" data-mp="3s4g7w2uwcctk">
                  <label htmlFor="name" className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-22129" data-mp="3s5rdm87wjunb">
                    Full Name
                  </label>
                  <input type="text" id="name" required className={`w-full px-3 py-2 rounded-md border focus:outline-none focus:ring-2 ${dk.inputOnCard} ${rt.inputFocus}`} data-id="element-22130" data-mp="4xdx31lpwrt1" />
                </div>
                <div data-id="element-22131" data-mp="9qyvgur83nsr">
                  <label htmlFor="email" className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-22132" data-mp="npy9z9l0umb7">
                    Email Address
                  </label>
                  <input type="email" id="email" required className={`w-full px-3 py-2 rounded-md border focus:outline-none focus:ring-2 ${dk.inputOnCard} ${rt.inputFocus}`} data-id="element-22133" data-mp="12c5hiqzn97fh" />
                </div>
              </div>
              <div data-id="element-22134" data-mp="ya0o9td3yctd">
                <label htmlFor="bookTitle" className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-22135" data-mp="y9iju9gaksad">
                  Book Title(s)
                </label>
                <input type="text" id="bookTitle" required placeholder="e.g. The Silent Echo, Dragon's Crown" className={`w-full px-3 py-2 rounded-md border focus:outline-none focus:ring-2 ${dk.inputOnCard} ${rt.inputFocus}`} data-id="element-22136" data-mp="oeyxkt36p2oo" />
              </div>
              <div data-id="element-22137" data-mp="5mo1bi31c2zj">
                <label htmlFor="message" className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-22138" data-mp="5l38py0a6u4b">
                  Message (Optional)
                </label>
                <textarea id="message" rows={4} placeholder="Tell us about your goals or specific products you're interested in..." className={`w-full px-3 py-2 rounded-md border focus:outline-none focus:ring-2 ${dk.inputOnCard} ${rt.inputFocus}`} data-id="element-22139" data-mp="2g4blo0twte1y" />
              </div>
              <button type="submit" className={`w-full flex justify-center items-center px-4 py-3 rounded-md text-white font-medium ${rt.primaryButton}`} data-id="element-22140" data-mp="2zuoultegadj5">
                <SendIcon className="h-4 w-4 mr-2" data-id="element-22141" data-mp="2q3kcwlxe4ekm" />
                Submit Application
              </button>
            </form>}
        </div>
      </div>
    </div>;
};
export default VendorLicensing;